import { DashboardSecretHandleSchema } from '@developerz/domain';
import { createEffect, createSignal, type JSX, Show } from 'solid-js';
import { t } from '../i18n';
import { Field } from './Field';
import { Modal } from './Modal';
import { Button, buttonClass } from './ui/Button';

// Add/rotate form for one vault credential (#312): a handle + the secret value,
// on the spec §3.5 `--lg` form dialog. The value renders as a password input
// and only lives in this local until POSTed and sealed server-side. The parent
// owns the mutation + busy/error state; `open === false` keeps it closed. The
// footer band sits INSIDE the form (submit button) and the form is the last
// template child (happy-dom #481). Mirrors ConnectIntegrationModal.

export type AddConnectionModalProps = {
  readonly open: boolean;
  readonly busy: boolean;
  readonly error: string | null;
  readonly onSubmit: (input: { name: string; value: string }) => void;
  readonly onClose: () => void;
};

export function AddConnectionModal(props: AddConnectionModalProps): JSX.Element {
  const [name, setName] = createSignal('');
  const [value, setValue] = createSignal('');

  // Reset the form on every open so values never bleed across credentials.
  createEffect(() => {
    if (props.open) {
      setName('');
      setValue('');
    }
  });

  const nameInvalid = (): boolean =>
    name() !== '' && !DashboardSecretHandleSchema.safeParse(name()).success;
  const incomplete = (): boolean =>
    value().trim() === '' || !DashboardSecretHandleSchema.safeParse(name()).success;

  const submit = (e: Event): void => {
    e.preventDefault();
    if (incomplete()) return;
    props.onSubmit({ name: name(), value: value() });
  };

  return (
    <Modal open={props.open} size="lg" title={t('connections.add_title')} onClose={props.onClose}>
      <form class="integration-form" onSubmit={submit}>
        <div class="field-group">
          <Field
            label={t('connections.name_label')}
            id="connection-name"
            desc={t('connections.name_hint')}
          >
            {(a11y) => (
              <input
                {...a11y}
                class="input"
                type="text"
                value={name()}
                onInput={(e) => setName(e.currentTarget.value)}
                autocomplete="off"
                required
                aria-invalid={nameInvalid() || undefined}
              />
            )}
          </Field>
          <Field label={t('connections.value_label')} id="connection-value">
            {(a11y) => (
              <input
                {...a11y}
                class="input"
                type="password"
                value={value()}
                onInput={(e) => setValue(e.currentTarget.value)}
                autocomplete="off"
                required
              />
            )}
          </Field>
        </div>
        <Show when={props.error}>
          <p class="field__error" role="alert">
            {props.error}
          </p>
        </Show>
        <div class="dialog__footer">
          <Button variant="outline" onClick={props.onClose}>
            {t('connections.cancel')}
          </Button>
          <button type="submit" class={buttonClass()} disabled={props.busy || incomplete()}>
            {props.busy ? t('connections.saving') : t('connections.save')}
          </button>
        </div>
      </form>
    </Modal>
  );
}
